/*
 * Hero Slideshow.
 *
 * Wraps the slide blocks in a Swiper carousel, cascades the fade-and-rise of
 * each part of the active slide, and parallaxes the background against the
 * page scroll. Requires swiper-bundle.min.js, which the section loads first.
 */
if (!customElements.get('hero-slideshow')) {
  customElements.define(
    'hero-slideshow',
    class HeroSlideshow extends HTMLElement {
      connectedCallback() {
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        /*
         * The slide on screen at page load is shown as-is: hiding it here and fading it back in
         * delayed the hero's first visible paint (Speed Index / LCP on mobile). Its class goes on
         * in the same task as the opt-in, so nothing transitions. Only slide changes animate.
         */
        const first = this.querySelector('.hero-slideshow__slide');
        if (first) first.classList.add('is-animated', 'is-initial');
        this.classList.add('hero-slideshow--animate');

        // Swiper arrives at page load (layout/theme.liquid); until then the first slide is static.
        window.whenSwiper(() => {
          if (this.isConnected) this.initSwiper();
        });
        this.initParallax();

        this.onBlockSelect = this.onBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.teardownParallax();

        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
      }

      /* Carousel ----------------------------------------------------------- */

      initSwiper() {
        const container = this.querySelector('.swiper');
        if (!container) return;

        this.slides = Array.from(this.querySelectorAll('.hero-slideshow__slide'));

        if (typeof Swiper === 'undefined' || this.slides.length < 2) {
          // One slide, or the library failed to load: the first slide is already showing.
          return;
        }

        const loop = this.dataset.loop === 'true';
        // Autoplay with nowhere to go would stall on the last slide.
        const autoplay = this.dataset.autoplay === 'true' && !this.reduceMotion.matches;
        const delay = (parseFloat(this.dataset.autoplaySpeed) || 6) * 1000;

        this.swiper = new Swiper(container, {
          slidesPerView: 1,
          speed: this.reduceMotion.matches ? 0 : 700,
          effect: 'fade',
          fadeEffect: { crossFade: true },
          // Off by default: the design disables the arrow at each end of the run.
          loop: loop,
          rewind: autoplay && !loop,
          autoplay: autoplay
            ? { delay: delay, disableOnInteraction: false, pauseOnMouseEnter: true }
            : false,
          watchSlidesProgress: true,
          navigation: {
            prevEl: this.querySelector('.hero-slideshow__arrow--prev'),
            nextEl: this.querySelector('.hero-slideshow__arrow--next'),
          },
          pagination: {
            el: this.querySelector('.hero-slideshow__pagination'),
            clickable: true,
          },
          keyboard: { enabled: true, onlyInViewport: true },
          a11y: {
            enabled: true,
            containerMessage: this.dataset.a11yLabel || undefined,
          },
          on: {
            // Play the new slide in while the outgoing one is still fading.
            slideChangeTransitionStart: (swiper) => {
              if (this.swiperReady) this.animateSlide(swiper.slides[swiper.activeIndex]);
            },
            // Reset the others only once they are invisible, so nothing jumps.
            slideChangeTransitionEnd: (swiper) => {
              const active = swiper.slides[swiper.activeIndex];
              if (!active) return;
              // Queried live: looping adds cloned slides that aren't in this.slides.
              this.querySelectorAll('.hero-slideshow__slide').forEach((slide) => {
                if (slide !== active) slide.classList.remove('is-animated');
              });
            },
          },
        });

        // With looping the active slide can be a clone; it is new, so this lands without a transition.
        const active = this.swiper.slides[this.swiper.activeIndex];
        if (active) active.classList.add('is-animated', 'is-initial');
        // Swiper can emit a transition event during construction; ignore those.
        this.swiperReady = true;

        // Count the first rotation from the page's load event rather than script start: rotating
        // while the page is still loading forces late layouts that inflate mobile Speed Index.
        if (autoplay && this.swiper.autoplay) {
          this.swiper.autoplay.stop();
          const start = () => this.swiper && this.swiper.autoplay.start();
          if (document.readyState === 'complete') start();
          else window.addEventListener('load', start, { once: true });
        }
      }

      animateSlide(slide) {
        if (!slide) return;

        // Later slides' images are deferred until page load; fetch them now if shown sooner.
        if (window.deferredMedia) slide.querySelectorAll('img[data-defer-src]').forEach(window.deferredMedia.restore);

        // Restart the cascade from zero even when re-entering the same slide.
        slide.classList.remove('is-animated', 'is-initial');
        // Forces a style flush so the class re-add is treated as a change.
        void slide.offsetWidth;
        slide.classList.add('is-animated');
      }

      /* Parallax ----------------------------------------------------------- */

      /*
       * The listener is only attached while the section is actually on screen,
       * and the compositor layer is only promoted for that window, so a hero
       * scrolled past costs nothing for the rest of the page.
       */
      initParallax() {
        this.background = this.querySelector('.hero-slideshow__background');
        if (!this.background || this.reduceMotion.matches) return;

        this.strength = parseFloat(this.dataset.parallaxStrength) || 60;
        this.onScroll = this.onScroll.bind(this);

        this.parallaxObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.startParallax();
            } else {
              this.stopParallax();
            }
          });
        });
        this.parallaxObserver.observe(this);
      }

      startParallax() {
        if (this.parallaxRunning) return;
        this.parallaxRunning = true;
        this.background.style.willChange = 'transform';
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.onScroll();
      }

      stopParallax() {
        if (!this.parallaxRunning) return;
        this.parallaxRunning = false;
        this.background.style.willChange = '';
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      teardownParallax() {
        if (this.parallaxObserver) this.parallaxObserver.disconnect();
        if (this.parallaxRunning) this.stopParallax();
        if (this.parallaxFrame) cancelAnimationFrame(this.parallaxFrame);
      }

      onScroll() {
        if (this.parallaxFrame) return;

        this.parallaxFrame = requestAnimationFrame(() => {
          this.parallaxFrame = null;

          const rect = this.getBoundingClientRect();
          const viewport = window.innerHeight;
          if (rect.bottom < 0 || rect.top > viewport) return;

          /*
           * -1 when the section sits a full travel below the viewport centre,
           * +1 when it sits a full travel above it, 0 when the two line up.
           */
          const travel = (viewport + rect.height) / 2;
          const progress = (viewport / 2 - (rect.top + rect.height / 2)) / travel;

          this.background.style.transform = `translate3d(0, ${(progress * this.strength).toFixed(2)}px, 0)`;
        });
      }

      /* Theme editor: hold on the slide the merchant is editing. */
      onBlockSelect(event) {
        if (!this.contains(event.target)) return;

        const index = Number(event.target.dataset.slideIndex);
        if (Number.isNaN(index)) return;

        if (this.swiper) {
          if (this.swiper.params.loop) {
            this.swiper.slideToLoop(index);
          } else {
            this.swiper.slideTo(index);
          }
          if (this.swiper.autoplay) this.swiper.autoplay.stop();
        } else {
          this.animateSlide(this.slides && this.slides[index]);
        }
      }
    }
  );
}
